import { useSyncExternalStore } from 'react';

export interface FinancialGrossUpSettings {
  readonly grossUpTaxPercent: number;
  readonly institutionalOverheadPercent: number;
  readonly defaultProfitMarginPercent: number;
  readonly longContractThresholdMonths: number;
}

const STORAGE_KEY = 'giyus.financial_grossup_settings.v1';

const DEFAULT_SETTINGS: FinancialGrossUpSettings = {
  grossUpTaxPercent: 14.25,
  institutionalOverheadPercent: 30,
  defaultProfitMarginPercent: 30,
  longContractThresholdMonths: 12,
};

let currentSettings: FinancialGrossUpSettings = loadFromStorage();
const listeners = new Set<() => void>();

function loadFromStorage(): FinancialGrossUpSettings {
  if (typeof window === 'undefined') {
    return DEFAULT_SETTINGS;
  }
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_SETTINGS;
    const parsed = JSON.parse(raw) as Partial<FinancialGrossUpSettings>;
    return {
      grossUpTaxPercent:
        typeof parsed.grossUpTaxPercent === 'number' && !Number.isNaN(parsed.grossUpTaxPercent)
          ? parsed.grossUpTaxPercent
          : DEFAULT_SETTINGS.grossUpTaxPercent,
      institutionalOverheadPercent:
        typeof parsed.institutionalOverheadPercent === 'number' &&
        !Number.isNaN(parsed.institutionalOverheadPercent)
          ? parsed.institutionalOverheadPercent
          : DEFAULT_SETTINGS.institutionalOverheadPercent,
      defaultProfitMarginPercent:
        typeof parsed.defaultProfitMarginPercent === 'number' &&
        !Number.isNaN(parsed.defaultProfitMarginPercent)
          ? parsed.defaultProfitMarginPercent
          : DEFAULT_SETTINGS.defaultProfitMarginPercent,
      longContractThresholdMonths:
        typeof parsed.longContractThresholdMonths === 'number' &&
        !Number.isNaN(parsed.longContractThresholdMonths)
          ? parsed.longContractThresholdMonths
          : DEFAULT_SETTINGS.longContractThresholdMonths,
    };
  } catch {
    return DEFAULT_SETTINGS;
  }
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function getSnapshot(): FinancialGrossUpSettings {
  return currentSettings;
}

export function updateFinancialGrossUpSettings(
  patch: Partial<FinancialGrossUpSettings>,
): FinancialGrossUpSettings {
  currentSettings = {
    ...currentSettings,
    ...patch,
  };
  if (typeof window !== 'undefined') {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(currentSettings));
    } catch {
      // Ignore storage quota errors
    }
  }
  for (const listener of listeners) {
    listener();
  }
  return currentSettings;
}

export function useFinancialGrossUpSettings(): {
  readonly settings: FinancialGrossUpSettings;
  readonly updateSettings: (patch: Partial<FinancialGrossUpSettings>) => FinancialGrossUpSettings;
} {
  const settings = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  return {
    settings,
    updateSettings: updateFinancialGrossUpSettings,
  };
}

/**
 * Calcula:
 * 1) Custo de Venda Real com Gross-Up da Nota Fiscal (sempre aplicado para não esquecer o imposto médio da NF)
 * 2) Valor da Margem de Lucro sobre o Custo
 * 3) Preço Sugerido de Venda Final (Custo + Gross-Up NF + Margem de Lucro)
 */
export function calculatePricingWithGrossUpAndMargin(
  baseCostBrl: number,
  grossUpTaxPercent: number,
  profitMarginPercent: number,
): {
  readonly baseCostBrl: number;
  readonly grossUpTaxPercent: number;
  readonly grossUpTaxAmountBrl: number;
  readonly realSaleCostWithGrossUpBrl: number;
  readonly profitMarginPercent: number;
  readonly profitMarginAmountBrl: number;
  readonly suggestedSalePriceBrl: number;
} {
  const cleanCost = Math.max(0, Math.round(baseCostBrl * 100) / 100);
  const cleanGrossUpPct = Math.max(0, grossUpTaxPercent);
  const cleanMarginPct = Math.max(0, profitMarginPercent);

  const grossUpTaxAmountBrl = Math.round(cleanCost * (cleanGrossUpPct / 100) * 100) / 100;
  const realSaleCostWithGrossUpBrl = Math.round((cleanCost + grossUpTaxAmountBrl) * 100) / 100;
  const profitMarginAmountBrl =
    Math.round(realSaleCostWithGrossUpBrl * (cleanMarginPct / 100) * 100) / 100;
  const suggestedSalePriceBrl =
    Math.round((realSaleCostWithGrossUpBrl + profitMarginAmountBrl) * 100) / 100;

  return {
    baseCostBrl: cleanCost,
    grossUpTaxPercent: cleanGrossUpPct,
    grossUpTaxAmountBrl,
    realSaleCostWithGrossUpBrl,
    profitMarginPercent: cleanMarginPct,
    profitMarginAmountBrl,
    suggestedSalePriceBrl,
  };
}
